import { z } from "zod";

// ---------------------------------------------------------------------------
// 自動ログアウトと本人確認（#189、docs/76。2026-10-16 射場さんの依頼）。共通の土台（現場力・営業力とも同じ）
//   ・無操作の上限は会社ごと（30分／1時間／2時間／8時間＝既定／24時間）。判定はサーバー（fn_session_validate）。画面のタイマーは案内だけ
//   ・期限の5分前に画面の上の帯、切れたら止めの画面（STOP_MSG.idle）。ログインのあとは直前の画面へ（next）
//   ・重い設定は本人確認（パスワード／パスキー／Google・Apple）。確認後10分は聞き直さない
//   文はここに1か所（画面の文・API の title）。言葉は docs/10 §5.1.2（取消・決定・設定・現在…）
// ---------------------------------------------------------------------------

/** 上限の選択肢（分）。DB の CHECK（tenants_auto_logout_minutes）と同じ */
export const AUTO_LOGOUT_MINUTES = [30, 60, 120, 480, 1440] as const;
export type AutoLogoutMinutes = (typeof AUTO_LOGOUT_MINUTES)[number];
export const AutoLogoutMinutes = z.literal(AUTO_LOGOUT_MINUTES, { error: "30分・1時間・2時間・8時間・24時間から選んでください" });
/** 既定（8時間） */
export const DEFAULT_AUTO_LOGOUT_MINUTES: AutoLogoutMinutes = 480;
export const AUTO_LOGOUT_LABEL: Record<AutoLogoutMinutes, string> = { 30: "30分", 60: "1時間", 120: "2時間", 480: "8時間", 1440: "24時間" };

/** GET /company/auto-logout の答え・PUT の本文 */
export const AutoLogoutSettings = z.object({ minutes: AutoLogoutMinutes });
export type AutoLogoutSettings = z.infer<typeof AutoLogoutSettings>;

/** 期限の何分前に帯を出すか */
export const AUTO_LOGOUT_WARN_SEC = 5 * 60;
/** 画面の操作（キー・クリック・タッチ・ホイール）から何秒以内に出た要求を「人の操作」とするか（X-User-Action: 1） */
export const USER_ACTION_WINDOW_MS = 15_000;
/** 画面の操作だけのとき（API を呼ばない入力・スクロール）、前に延ばしてから何秒たったら POST /auth/alive を1本送るか */
export const ALIVE_EVERY_MS = 60_000;
/** API に送る「人の操作」の印 */
export const USER_ACTION_HEADER = "x-user-action";
/** API の答えのヘッダー：無操作で切れるまでの残り（秒） */
export const IDLE_LEFT_HEADER = "x-gb-idle-left";
/** 無操作で切れた（API の 401 の code） */
export const SESSION_IDLE_CODE = "SESSION_IDLE";

export const AUTO_LOGOUT_MSG = {
  /** ① 設定の画面 */
  title: "自動ログアウト",
  lead: "操作のない状態が続いた場合、自動的にログアウトします。",
  defaultMark: "（既定）",
  note: "変更は全ユーザーに適用されます。",
  cancel: "取消",
  confirm: "決定",
  saved: (label: string) => `自動ログアウトを${label}に設定しました`,
  /** ② 期限の5分前の帯。left＝「4:59」 */
  warn: (left: string) => `まもなく自動ログアウトします（残り ${left}）`,
  /** 帯の読み上げ（毎秒の残りは読ませない） */
  warnSr: "まもなく自動ログアウトします",
  keep: "ログイン継続",
  /** ④ 再ログインのあと、退避した入力を戻した（右下の通知） */
  restored: "保存されていない入力を復元しました。",
  /** API の 401 SESSION_IDLE の title（画面は止めの画面を出すので、ほかの所にはふつう出ない） */
  api: "一定時間操作がなかったため、ログアウトしました。再度ログインしてください",
} as const;

/** 残り秒 → 「4:59」（分:秒。1時間を超えても分で数える） */
export function countdownText(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// ログインのあとに戻る画面（next）。同じサイトの道だけ（docs/76 §1.2 c）
// ---------------------------------------------------------------------------

/**
 * next に使える道か：「/」で始まり「//」で始まらない・バックスラッシュと制御文字を含まない・500字まで。
 * ほかのサイトへ移る形（//evil.example・/\evil・https://…）は null（呼ぶ側は /home へ）
 */
export function safeNextPath(p: unknown): string | null {
  if (typeof p !== "string" || p.length === 0 || p.length > 500) return null;
  if (!p.startsWith("/") || p.startsWith("//")) return null;
  if (p.includes("\\") || /[\u0000-\u001f\u007f]/.test(p)) return null;
  return p;
}

/**
 * /login?next=…（next が使えなければ /login だけ。ログインの画面・止めの画面への道は next にしない）。
 * /handoff も next にしない（#192、docs/76 §0.3 ト：鍵は URL の # の後ろで、開いた瞬間に消える＝ログインのあとに戻っても鍵が無く「切り替えられませんでした」になる）
 */
export function loginPathWithNext(path: string | null | undefined): string {
  const n = safeNextPath(path);
  if (!n || /^\/(login|signup|choose|welcome|oauth\/|handoff(?:[/?#]|$))/.test(n)) return "/login";
  return `/login?next=${encodeURIComponent(n)}`;
}

// ---------------------------------------------------------------------------
// 本人確認（⑤）
// ---------------------------------------------------------------------------

/** 本人確認が効く時間（分） */
export const REAUTH_MINUTES = 10;
/** 本人確認が要る（API の 403 の code） */
export const REAUTH_REQUIRED_CODE = "REAUTH_REQUIRED";

/** GET /auth/reauth-status：fresh＝10分以内に確認済み／methods＝この人が使える確かめ方（パスキーはこの製品のドメインのもの） */
export const ReauthStatus = z.object({
  fresh: z.boolean(),
  until: z.string().nullable(),
  methods: z.object({ password: z.boolean(), google: z.boolean(), apple: z.boolean(), passkey: z.boolean() }),
});
export type ReauthStatus = z.infer<typeof ReauthStatus>;

/** POST /auth/reauth（パスワード） */
export const ReauthPasswordRequest = z.object({ password: z.string().min(1, "パスワードを入力してください").max(200) });
export type ReauthPasswordRequest = z.infer<typeof ReauthPasswordRequest>;
/** POST /auth/reauth/passkey（WebAuthn の答え） */
export const ReauthPasskeyRequest = z.object({ response: z.record(z.string(), z.unknown()) });
export type ReauthPasskeyRequest = z.infer<typeof ReauthPasskeyRequest>;

/** パスワードの無い人の窓の文（names＝Google／Apple／パスキー のうち持っているもの。英字の前後は空白で区切る） */
function reauthViaText(names: string[]): string {
  const n = names.join("／");
  const pre = /^[A-Za-z]/.test(n) ? " " : "";
  const post = /[A-Za-z]$/.test(n) ? " " : "";
  return `この操作には${pre}${n}${post}での再認証が必要です。`;
}

export const REAUTH_MSG = {
  title: "本人確認",
  password: "この操作にはパスワードの入力が必要です。",
  /** パスワードの無い人（持っている方法：Google／Apple／パスキー の順） */
  via: (m: { google: boolean; apple: boolean; passkey: boolean }) =>
    reauthViaText([...(m.google ? ["Google"] : []), ...(m.apple ? ["Apple"] : []), ...(m.passkey ? ["パスキー"] : [])]),
  passwordLabel: "パスワード",
  /** パスワードのある人もパスキーで確かめられる（窓の下の脇のボタン） */
  passkey: "パスキーで続ける",
  cancel: "取消",
  confirm: "決定",
  wrong: "パスワードが違います",
  passwordRequired: "パスワードを入力してください",
  locked: "パスワードの間違いが続いたため、30分ほど本人確認ができません",
  /** 窓を閉じた（元の操作の文） */
  cancelled: "本人確認を取り消しました",
  /** Google・Apple の窓が開かなかった（ポップアップを止めている） */
  popupBlocked: "本人確認の画面を開けませんでした。もう一度押してください",
  /** Google・Apple から戻った画面（/oauth/reauth） */
  finishing: "本人確認をしています…",
  done: "本人確認が完了しました",
  /** 別の窓が自動で閉じなかったとき */
  closeWindow: "この画面を閉じてください。",
  failed: "本人確認ができませんでした",
  /** API の 403 REAUTH_REQUIRED の title */
  api: "この操作には本人確認が必要です",
  /** パスワードもパスキーも Google・Apple も無い（ふつうは無い） */
  noMethod: "本人確認の方法がありません。ログアウトしてから再度ログインしてください",
} as const;
